import { Icon } from '@99designs/common/components/Icon';
import { FormattedHTMLMessage } from '@99designs/common/i18n';
import { SquarespaceApiBrief } from '__generated__/SquarespaceApiBrief';
import { BriefState } from '__generated__/types';
import Heading from 'common/components/Heading';
import { DirectLoginModal } from 'common/components/SquarespaceModal';
import { handlePromise } from 'common/utils/Bugsnag';
import { Source } from 'launch/consts/signupSource';
import { AuthContext } from 'projects/components/AuthContext';
import BriefContext from 'projects/components/BriefContext';
import HeaderUserLayout from 'projects/components/HeaderUserLayout';
import SquarespaceLaunchModal, { LaunchState } from 'projects/components/SquarespaceLaunchModal';
import { Color } from 'projects/consts';
import { trackViewedBriefEvent } from 'projects/events';
import { redirectLaunchedStructuredBrief } from 'projects/routes';
import qs from 'qs';
import React, { useContext, useEffect } from 'react';
import { useApolloClient } from 'react-apollo';
import { useLocation } from 'react-router';
import useWorkflowMutations from './useWorkflowMutations';

function useSquarespaceLogin() {
    const client = useApolloClient();
    const { login } = useContext(AuthContext);
    const location = useLocation();

    return async function squarespaceLogin() {
        const props = {
            source: Source.SQUARESPACE_LAUNCH,
            signupHeading: (
                <div
                    style={{ width: '75px' }}
                    dangerouslySetInnerHTML={{
                        __html: require('@99designs/common/core/images/99d-logomark-in-square.svg'),
                    }}
                />
            ),
            signupMessage: (
                <>
                    <Heading size="h3" withDivider={true}>
                        {__('Welcome to 99designs!')}
                    </Heading>

                    <div className="paragraph">
                        <FormattedHTMLMessage
                            message={__(
                                "Let's create your 99designs account so you can start collaborating on your Squarespace website."
                            )}
                        />
                    </div>
                </>
            ),
            ctaMessage: __('Create account'),
            loginLinkMessage: __('Already have a 99designs account?'),
            showHeaderDivider: false,
            modalComponent: DirectLoginModal,
            postLogin: async () => {
                await client.resetStore();
            },
        };

        // This is a "temporary" method for prefilling a user's email from
        // squarespace via query parameter.
        const query = location.search.slice(1);
        const { email } = qs.parse(query);

        return login({ email, ...props });
    };
}

export interface Props {
    brief: SquarespaceApiBrief;
}

/**
 * Squarespace project creation flow for an existing brief with *pre-selected* designer(s).
 */
function DirectWorkflow({ brief }: Props) {
    const location = useLocation();
    const [launchState, launch, claim] = useWorkflowMutations(brief);
    const login = useSquarespaceLogin();
    const { user } = useContext(AuthContext);

    function trackEvent() {
        const query = location.search.slice(1);
        const { email } = qs.parse(query);

        trackViewedBriefEvent({
            id: brief.id,
            page: 'SquarespaceDirect',
            initial_email_address: email,
        });
    }

    useEffect(() => {
        trackEvent();

        handlePromise(
            (async function() {
                await login();
                await claim();
                await launch({ delay: 2000 });
            })(),
            DirectWorkflow.name
        );
    }, []);

    if (brief.state == BriefState.LAUNCHED && !launchState) {
        redirectLaunchedStructuredBrief(brief);
        return null;
    }

    if (!user) {
        return null;
    }

    const showModal = launchState && [LaunchState.Error, LaunchState.Success].includes(launchState);

    return (
        <BriefContext.Provider value={{ brief, themeColor: Color.squarespaceBlack }}>
            <div className="sqsp-launch">
                <div>
                    <div className="sqsp-launch__icons">
                        <div
                            className="sqsp-launch__icon"
                            dangerouslySetInnerHTML={{
                                __html: require('!svg-inline-loader!images/squarespace/logo.svg'),
                            }}
                        />

                        <div className="sqsp-launch__animation">
                            <Icon type="right-2" />
                        </div>

                        <div
                            className="sqsp-launch__icon"
                            dangerouslySetInnerHTML={{
                                __html: require('@99designs/common/core/images/99d-logomark-in-square.svg'),
                            }}
                        />
                    </div>

                    <HeaderUserLayout users={brief.designers} />
                </div>
            </div>

            <SquarespaceLaunchModal state={launchState!} brief={brief} showing={!!showModal} />
        </BriefContext.Provider>
    );
}

export default DirectWorkflow;
